import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Chip,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import { ReactComponent as ChipDeleteIcon } from "../shared/images/icons/chip-delete.svg";
import { ReactComponent as ClearAllIcon } from "../shared/images/icons/clear-all.svg";
import { ReactComponent as SearchIconRight } from "../shared/images/icons/search-icon-right.svg";
import ArrowDropdownIcon from "../shared/images/icons/arrowDropdown.svg";
import { useWiserTheme } from "../hooks/useWiserTheme";
import { getDropdownPanelSx, getOutlinedFilterInputSx, getFilterChipSx, getFilterCountChipSx, getFilterPlaceholderColor, getFilterTriggerBorder, resolveFilterTriggerBg } from "../shared/theme/wiserThemeHelpers";

const dropdownSearchFieldSx = {
  mb: "8px",
  "& .MuiOutlinedInput-root": {
    height: 48,
    padding: "0 12px 0 4px",
    paddingRight: 0,
    background: "#FFFFFF",
    borderRadius: "4px",
    fontFamily: "Inter, sans-serif",
    "& fieldset": {
      border: "1px solid #CBD5E1",
    },
    "&:hover fieldset": {
      border: "1px solid #CBD5E1",
    },
    "&.Mui-focused fieldset": {
      border: "1px solid #0065FF",
      borderWidth: "1px",
    },
    "& .MuiOutlinedInput-input": {
      fontFamily: "Inter, sans-serif",
      fontWeight: 400,
      fontSize: "16px",
      lineHeight: "24px",
      letterSpacing: 0,
      color: "#172B4D",
      "&::placeholder": {
        fontFamily: "Inter, sans-serif",
        fontWeight: 400,
        fontSize: "16px",
        lineHeight: "24px",
        letterSpacing: 0,
        color: "#64748B",
        opacity: 1,
      },
    },
  },
};

interface Option {
  id: string;
  name: string;
}

interface SearchableAutocompleteMultiProps {
  options: Option[];
  value: Option[];
  onChange: (options: Option[]) => void;
  placeholder?: string;
  disabled?: boolean;
  minWidth?: number;
  /** Tetikleyici sabit genişlik (içerik/chip ile büyümez) */
  width?: number | string;
  searchPlaceholder?: string;
  maxVisibleChips?: number;
  fullWidth?: boolean;
  backgroundColor?: string;
  showBorder?: boolean;
  height?: number | string;
}

/** Navbar (999) altında; loading backdrop (drawer+1 ≈ 1201) üstünde kalmaması için 900 */
const menuZIndex = 900;

/** Menü padding + satır px + dikey scrollbar payı */
const DROPDOWN_WIDTH_EXTRA = 80;
const MENU_MEASURE_FONT = "14px Inter, sans-serif";

const SearchableAutocompleteMulti: React.FC<
  SearchableAutocompleteMultiProps
> = ({
  options,
  value,
  onChange,
  placeholder = "Seçiniz",
  disabled = false,
  minWidth = 200,
  width,
  searchPlaceholder = "Ara...",
  maxVisibleChips = 2,
  fullWidth = false,
  backgroundColor = "#F4F5F7",
  showBorder = false,
  height = 40,
}) => {
    const { darkModeActive, tokens } = useWiserTheme();
    const resolvedTriggerBg = resolveFilterTriggerBg(backgroundColor, darkModeActive);
    const dropdownSearchFieldSx = {
      mb: "8px",
      ...getOutlinedFilterInputSx(darkModeActive),
      "& .MuiOutlinedInput-root": {
        height: 48,
        padding: "0 12px 0 4px",
        paddingRight: 0,
        background: tokens.inputBg,
        borderRadius: "4px",
        fontFamily: "Inter, sans-serif",
        "& fieldset": {
          borderColor: darkModeActive ? "transparent" : tokens.borderStrong,
        },
        "&:hover fieldset": {
          borderColor: darkModeActive ? "transparent" : tokens.textMuted,
        },
        "&.Mui-focused fieldset": {
          borderColor: tokens.accent,
          borderWidth: "1px",
        },
        "& .MuiOutlinedInput-input": {
          color: tokens.textPrimary,
          "&::placeholder": {
            color: tokens.textMuted,
            opacity: 1,
          },
        },
      },
    };
    const optionLabelSx = {
      fontFamily: "Inter, sans-serif",
      fontSize: 14,
      color: darkModeActive ? tokens.textPrimary : "#374151",
      lineHeight: 1.4,
      whiteSpace: "normal" as const,
      overflowWrap: "break-word" as const,
      wordBreak: "normal" as const,
    };
    const [search, setSearch] = useState("");
    const [open, setOpen] = useState(false);
    const [dropdownWidth, setDropdownWidth] = useState(minWidth);
    const rootRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (options.length > 0) {
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        if (context) {
          context.font = "14px Inter, sans-serif";

          let maxWidth = minWidth;
          options.forEach((option) => {
            if (option?.name) {
              const textWidth = context.measureText(option.name).width;
              const totalWidth = Math.ceil(textWidth) + DROPDOWN_WIDTH_EXTRA;
              maxWidth = Math.max(maxWidth, totalWidth);
            }
          });

          const finalWidth = Math.min(maxWidth, 400);
          setDropdownWidth(finalWidth);
        }
      }
    }, [options, minWidth]);

    useEffect(() => {
      if (!open) return;

      function handleClickOutside(event: MouseEvent) {
        if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
          setOpen(false);
          setSearch("");
        }
      }

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [open]);

    const normalizeTurkish = (text: string): string => {
      return text
        .replace(/İ/g, "i")
        .replace(/I/g, "ı")
        .replace(/Ş/g, "s")
        .replace(/Ğ/g, "g")
        .replace(/Ü/g, "u")
        .replace(/Ö/g, "o")
        .replace(/Ç/g, "c")
        .toLowerCase()
        .replace(/ı/g, "i");
    };

    const filteredOptions = options.filter(
      (option) =>
        option &&
        option.name &&
        normalizeTurkish(option.name).includes(normalizeTurkish(search)),
    );

    const handleOptionClick = (option: Option) => {
      if (value.some((v) => v.id === option.id)) {
        onChange(value.filter((v) => v.id !== option.id));
      } else {
        onChange([...value, option]);
      }
    };

    const hasValue = value && value.length > 0;
    const visibleItems = value.slice(0, maxVisibleChips);
    const hiddenItems = value.slice(maxVisibleChips);
    const remainingCount = hiddenItems.length;
    const chipCompact = remainingCount > 0;

    const triggerWidth = width ?? (fullWidth ? "100%" : undefined);

    return (
      <Box
        sx={{
          position: "relative",
          minWidth: width ?? minWidth,
          ...(width !== undefined && { width, maxWidth: width }),
          ...(fullWidth && width === undefined && { width: "100%" }),
        }}
        ref={rootRef}
      >
        <Box
          sx={{
            minWidth: width ?? minWidth,
            width: triggerWidth,
            maxWidth: width,
            height,
            background: disabled
              ? resolvedTriggerBg
              : open
                ? darkModeActive
                  ? tokens.surface
                  : "#FFFFFF"
                : resolvedTriggerBg,
            borderRadius: "3px",
            border: getFilterTriggerBorder(darkModeActive, {
              open,
              disabled,
              showBorder,
            }),
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            flexWrap: "nowrap",
            gap: 0.5,
            px: 2,
            cursor: disabled ? "not-allowed" : "pointer",
            position: "relative",
            transition:
              "background 200ms ease-in-out, border-color 200ms ease-in-out",
            "&:hover": {
              background: disabled
                ? resolvedTriggerBg
                : open
                  ? darkModeActive
                    ? tokens.surface
                    : "#FFFFFF"
                  : darkModeActive
                    ? tokens.hoverBg
                    : "#EBECF0",
            },
          }}
          onClick={() => !disabled && setOpen(!open)}
          aria-disabled={disabled}
        >
          {hasValue ? (
            <Box
              sx={{
                display: "flex",
                flexWrap: "nowrap",
                gap: "4px",
                flex: 1,
                minWidth: 0,
                maxWidth:
                  !disabled ? "calc(100% - 48px)" : "calc(100% - 32px)",
                alignItems: "center",
                overflow: "hidden",
              }}
            >
              {visibleItems.map((selected) => (
                <Tooltip key={selected.id} title={selected.name} arrow>
                  <Chip
                    label={selected.name}
                    deleteIcon={
                      <ChipDeleteIcon width={16} height={16} aria-hidden />
                    }
                    onDelete={
                      disabled
                        ? undefined
                        : (e) => {
                          e.stopPropagation();
                          onChange(value.filter((v) => v.id !== selected.id));
                        }
                    }
                    sx={getFilterChipSx(darkModeActive, disabled, {
                      compact: chipCompact,
                    })}
                  />
                </Tooltip>
              ))}
              {remainingCount > 0 && (
                <Tooltip
                  title={
                    <Box
                      sx={{
                        m: 0,
                        p: 0,
                      }}
                    >
                      {hiddenItems.map((item) => (
                        <Box
                          key={item.id}
                          sx={{
                            py: 0.25,
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                          }}
                        >
                          {item.name}
                        </Box>
                      ))}
                    </Box>
                  }
                  arrow
                >
                  <Chip
                    label={`+${remainingCount}`}
                    sx={getFilterCountChipSx(darkModeActive, disabled)}
                  />
                </Tooltip>
              )}
            </Box>
          ) : (
            <Typography
              sx={{
                fontFamily:
                  '"SF Pro Text", -apple-system, BlinkMacSystemFont, Inter, sans-serif',
                color: getFilterPlaceholderColor(darkModeActive, disabled),
                fontSize: 14,
                fontWeight: 400,
                lineHeight: "20px",
                flex: 1,
                minWidth: 0,
              }}
            >
              {placeholder}
            </Typography>
          )}

          <Box
            sx={{
              position: "absolute",
              right: 8,
              top: "50%",
              transform: "translateY(-50%)",
              display: "flex",
              alignItems: "center",
            }}
          >
            {hasValue && !disabled && (
              <Box
                component="button"
                type="button"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  onChange([]);
                }}
                aria-label="Tümünü temizle"
                sx={{
                  width: 16,
                  height: 16,
                  minWidth: 16,
                  minHeight: 16,
                  padding: 0,
                  margin: 0,
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  color: "#A5ADBA",
                  borderRadius: "2px",
                  "&:hover": { color: "#42526E" },
                  "&:focus-visible": {
                    outline: "2px solid #0052CC",
                    outlineOffset: 2,
                  },
                  "& svg": { display: "block", width: 16, height: 16 },
                }}
              >
                <ClearAllIcon width={16} height={16} aria-hidden />
              </Box>
            )}
            <Box
              sx={{
                pointerEvents: "none",
                color: disabled ? tokens.textMuted : tokens.textPrimary,
                display: "flex",
                alignItems: "center",
              }}
            >
              <img
                src={ArrowDropdownIcon}
                alt=""
                width={24}
                height={24}
              style={{
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease",
                filter: darkModeActive ? "brightness(0) invert(1)" : "none",
              }}
              />
            </Box>
          </Box>
        </Box>

        {open && (
          <Box
            sx={{
              position: "absolute",
              top: "calc(100% + 4px)",
              left: 0,
              right: fullWidth ? 0 : "auto",
              width: fullWidth ? "100%" : dropdownWidth,
              zIndex: menuZIndex,
              maxHeight: 300,
              overflow: "hidden",
              p: 1,
              ...getDropdownPanelSx(darkModeActive),
            }}
          >
            <TextField
              inputRef={searchInputRef}
              fullWidth
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              disabled={disabled}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ mr: 0.5 }}>
                    <SearchIconRight width={24} height={24} aria-hidden />
                  </InputAdornment>
                ),
              }}
              sx={dropdownSearchFieldSx}
            />

            <Box
              sx={{
                maxHeight: 220,
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <Box
                    key={option.id}
                    sx={{
                      px: 2,
                      py: 1,
                      cursor: "pointer",
                      "&:hover": {
                        background: darkModeActive ? tokens.hoverBg : "#F3F4F6",
                      },
                      backgroundColor: value.some((v) => v.id === option.id)
                        ? darkModeActive
                          ? "rgba(91, 124, 250, 0.2)"
                          : "#E3F2FD"
                        : "transparent",
                      minHeight: 32,
                      display: "flex",
                      alignItems: "center",
                    }}
                    onClick={() => handleOptionClick(option)}
                  >
                    <Typography sx={optionLabelSx}>
                      {option?.name || "- İSİMSİZ -"}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Box sx={{ px: 2, py: 2 }}>
                  <Typography
                    sx={{ fontSize: 14, color: tokens.textMuted, textAlign: "center" }}
                  >
                    Sonuç bulunamadı
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

export default SearchableAutocompleteMulti;
